import React, { useState, useEffect } from 'react';
import { Modal, Form, Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FaUpload } from 'react-icons/fa';
import '../Styles/AddTestKitchenItemModal.css';

const splitLines = (text) =>
  (text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

// 'YYYY-MM-DD' for the date input, whether the API sends a date or an ISO datetime
const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

const schema = yup.object().shape({
  is_recommended: yup.boolean(),
  sort_order: yup
    .number()
    .transform((v, orig) => (orig === '' || orig === null ? 0 : v))
    .typeError('Order must be a number')
    .integer('Order must be a whole number')
    .min(0, 'Order must be 0 or more'),
  name: yup.string().trim().required('Name is required').max(120, 'Name must be 120 characters or fewer'),
  category: yup.string().trim().required('Category is required').max(120, 'Category must be 120 characters or fewer'),
  why_text: yup.string().trim().required('Why we recommend them is required').max(1200, 'Keep this to 1200 characters or fewer'),
  what_they_do: yup
    .string()
    .test('count', 'Add three to six lines', (v) => {
      const n = splitLines(v).length;
      return n >= 3 && n <= 6;
    })
    .test('len', 'Each line must be 80 characters or fewer', (v) => splitLines(v).every((l) => l.length <= 80)),
  member_offer: yup
    .string()
    .nullable()
    .max(160, 'Keep the offer to 160 characters or fewer')
    .test('nodollar', 'No dollar figures in the offer line.', (v) => !v || !/\$\s?\d/.test(v)),
  offer_ends: yup.string().nullable(),
  get_started_url: yup.string().nullable().trim().url('Enter a valid URL, including https://'),
  website_url: yup.string().trim().required('Website URL is required').url('Enter a valid URL, including https://'),
  logo: yup
    .mixed()
    .test('fileSize', 'Logo file size is too large (max 2MB)', (v) => !v || v.size <= 2 * 1024 * 1024)
    .test('fileType', 'Only JPG, PNG and WebP images are allowed', (v) => !v || ['image/jpeg', 'image/png', 'image/webp'].includes(v.type)),
});

const emptyValues = {
  is_recommended: true,
  sort_order: 0,
  name: '',
  category: '',
  why_text: '',
  what_they_do: '',
  member_offer: '',
  offer_ends: '',
  get_started_url: '',
  website_url: '',
};

const AddRecommendedPartnerModal = ({ show, onHide, item, onSave }) => {
  const isEditing = !!item;
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [serverErrors, setServerErrors] = useState({});

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
    resolver: yupResolver(schema),
    defaultValues: emptyValues,
  });

  useEffect(() => {
    setError('');
    setServerErrors({});
    setLogoFile(null);
    setRemoveLogo(false);
    if (item) {
      setLogoPreview(item.presigned_logo_url || null);
      reset({
        is_recommended: !!item.is_recommended,
        sort_order: item.sort_order ?? 0,
        name: item.name || '',
        category: item.category || '',
        why_text: item.why_text || '',
        what_they_do: Array.isArray(item.what_they_do) ? item.what_they_do.join('\n') : '',
        member_offer: item.member_offer || '',
        offer_ends: toDateInput(item.offer_ends),
        get_started_url: item.get_started_url || '',
        website_url: item.website_url || '',
      });
    } else {
      setLogoPreview(null);
      reset(emptyValues);
    }
  }, [item, show, reset]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setRemoveLogo(false);
      setValue('logo', file, { shouldValidate: true });
    } else {
      setLogoFile(null);
      setLogoPreview(item?.presigned_logo_url || null);
      setValue('logo', null, { shouldValidate: true });
    }
  };

  const fieldError = (name) => {
    const client = errors[name]?.message;
    if (client) return client;
    if (name === 'what_they_do') {
      const key = Object.keys(serverErrors).find((k) => k === 'what_they_do' || k.startsWith('what_they_do.'));
      return key ? serverErrors[key][0] : null;
    }
    return serverErrors[name]?.[0] || null;
  };

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setError('');
    setServerErrors({});
    try {
      const formData = new FormData();
      formData.append('name', data.name.trim());
      formData.append('category', data.category.trim());
      formData.append('why_text', data.why_text.trim());
      splitLines(data.what_they_do).forEach((line) => formData.append('what_they_do[]', line));
      formData.append('member_offer', data.member_offer || '');
      formData.append('offer_ends', data.offer_ends || '');
      formData.append('get_started_url', data.get_started_url || '');
      formData.append('website_url', data.website_url.trim());
      formData.append('is_recommended', data.is_recommended ? '1' : '0');
      formData.append('sort_order', String(data.sort_order ?? 0));
      if (logoFile instanceof File) {
        formData.append('logo', logoFile);
      }
      if (removeLogo) {
        formData.append('remove_logo', '1');
      }

      await onSave(formData);
      onHide();
    } catch (err) {
      if (err?.data?.errors) {
        setServerErrors(err.data.errors);
      }
      setError(err?.data?.message || err.message || 'Failed to save partner. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const textField = (name, label, props = {}, help) => (
    <Col xs={12}>
      <div className="form-control">
        <label>
          {label} {props.required && <span className="red-star">*</span>}
        </label>
        <br />
        <input
          type={props.type || 'text'}
          placeholder={props.placeholder}
          min={props.min}
          className={fieldError(name) ? 'is-invalid' : ''}
          {...register(name)}
        />
        {help && <small className="text-muted d-block">{help}</small>}
        {fieldError(name) && <p className="error-text">{fieldError(name)}</p>}
      </div>
    </Col>
  );

  const textArea = (name, label, rows, help, required) => (
    <Col xs={12}>
      <div className="form-control">
        <label>
          {label} {required && <span className="red-star">*</span>}
        </label>
        <br />
        <textarea rows={rows} className={fieldError(name) ? 'is-invalid' : ''} {...register(name)} />
        {help && <small className="text-muted d-block">{help}</small>}
        {fieldError(name) && <p className="error-text">{fieldError(name)}</p>}
      </div>
    </Col>
  );

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Body>
        <h4>{isEditing ? 'Edit partner' : 'Add partner'}</h4>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            {/* Recommended */}
            <Col xs={12} className="mt-2">
              <div className="form-control" style={{ padding: '0.75rem 1rem' }}>
                <div className="d-flex align-items-center">
                  <label className="me-2 mb-0" style={{ fontSize: '0.9rem', fontWeight: '500' }}>Recommended</label>
                  <label className="switch mb-0">
                    <input type="checkbox" {...register('is_recommended')} />
                    <span className="slider round"></span>
                  </label>
                  <span className="ms-2" style={{ fontSize: '0.9rem' }}>
                    {watch('is_recommended') ? 'On' : 'Off'}
                  </span>
                </div>
                <small className="text-muted d-block">
                  On shows the partner to every cafe member. Off removes it from the list and its page.
                </small>
              </div>
            </Col>

            {textField('sort_order', 'Order', { type: 'number', min: 0 }, 'Lower numbers show first.')}
            {textField('name', 'Name', { required: true }, 'Used in the buttons, e.g. "Get started with Square".')}
            {textField('category', 'Category', { required: true, placeholder: 'Point of sale and payments' })}

            {/* Logo */}
            <Col xs={12}>
              <Form.Group controlId="partner_logo" className="mb-3">
                <Form.Label>Logo</Form.Label>
                <div className="file-upload-container d-flex">
                  <label className="file-upload-label">
                    <input
                      type="file"
                      id="partner_logo"
                      accept="image/jpeg, image/png, image/webp"
                      onChange={handleLogoChange}
                      className={`file-upload-input ${fieldError('logo') ? 'is-invalid' : ''}`}
                      style={{ display: 'none' }}
                    />
                    <div><FaUpload className="file-upload-icon" /></div>
                    <div>
                      <span className="file-upload-text">
                        {logoFile ? logoFile.name : 'Click to select a logo'}
                      </span>
                      <span className="format-text">(JPG, PNG or WebP, max 2MB)</span>
                      {fieldError('logo') && <div className="error-text mt-1">{fieldError('logo')}</div>}
                    </div>
                  </label>
                </div>
                <small className="text-muted d-block">Grey placeholder until a logo is uploaded.</small>
                {logoPreview && !removeLogo && (
                  <div className="file-preview mt-2">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      style={{ width: '60px', height: '60px', objectFit: 'contain', borderRadius: '4px', marginRight: '10px', border: '1px solid #ddd' }}
                    />
                    <span>{logoFile?.name || 'Current logo'}</span>
                  </div>
                )}
                {isEditing && item?.presigned_logo_url && !logoFile && (
                  <div className="form-check mt-2">
                    <input
                      type="checkbox"
                      id="remove_logo"
                      className="form-check-input"
                      checked={removeLogo}
                      onChange={(e) => setRemoveLogo(e.target.checked)}
                    />
                    <label htmlFor="remove_logo" className="form-check-label">Remove logo</label>
                  </div>
                )}
              </Form.Group>
            </Col>

            {textArea('why_text', 'Why we recommend them', 4, 'Two to three sentences in our words. Shown on the card and the detail page.', true)}
            {textArea('what_they_do', 'What they do', 5, 'Three to six short lines. The card shows the first three, the detail page shows all.', true)}
            {textField('member_offer', 'Member offer', {}, 'One line, no dollar figures. Start with a capital, the card shows it as "Member offer, ...". Leave empty for no offer.')}
            {textField('offer_ends', 'Offer ends', { type: 'date' }, 'The offer is hidden after this date.')}
            {textField('get_started_url', 'Get started URL', { type: 'url' }, "The partner's own sign-up or enquiry form. Leave empty to hide the Get started button.")}
            {textField('website_url', 'Website URL', { type: 'url', required: true })}

            {error && (
              <Col xs={12}>
                <div className="alert alert-danger">{error}</div>
              </Col>
            )}
          </Row>

          <div className="btn-action mt-4">
            <button type="button" className="btn-outline-black" onClick={onHide} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving...</>
              ) : isEditing ? 'Update partner' : 'Add partner'}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default AddRecommendedPartnerModal;
